import type { PaginationUtils } from "../types/components.types";
import type { TrackingPaginationItem } from "../types/pagination.types";

export const getTrackingPaginationItems = ({
	currentPage,
	totalPages,
}: PaginationUtils): TrackingPaginationItem[] => {
	if (totalPages <= 1) {
		return [{ type: "page", value: 1, isCurrent: true }];
	}

	if (totalPages <= 4) {
		return Array.from({ length: totalPages }, (_, index) => {
			const value = index + 1;
			return {
				type: "page",
				value,
				isCurrent: value === currentPage,
			} as const;
		});
	}

	const clampedPage = Math.max(1, Math.min(currentPage, totalPages));
	const visiblePages = new Set<number>([1, totalPages]);

	if (clampedPage <= 2) {
		visiblePages.add(2);
		visiblePages.add(3);
	} else if (clampedPage >= totalPages - 1) {
		visiblePages.add(totalPages - 2);
		visiblePages.add(totalPages - 1);
	} else {
		visiblePages.add(clampedPage - 1);
		visiblePages.add(clampedPage);
		visiblePages.add(clampedPage + 1);
	}

	const orderedPages = Array.from(visiblePages)
		.filter((page) => page >= 1 && page <= totalPages)
		.sort((a, b) => a - b);

	const items: TrackingPaginationItem[] = [];

	for (let index = 0; index < orderedPages.length; index += 1) {
		const page = orderedPages[index];
		const previousPage = orderedPages[index - 1];

		if (previousPage && page - previousPage > 1) {
			items.push({
				type: "ellipsis",
				key: previousPage === 1 ? "leading" : "trailing",
				startPage: previousPage + 1,
				endPage: page - 1,
			});
		}

		items.push({
			type: "page",
			value: page,
			isCurrent: page === clampedPage,
		});
	}

	return items;
};
